import { Injectable } from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { DayOrWeekCalendarModel } from '../../../../../../../../../core/models/calendar/day-or-week-calendar.model';
import { TimeBlockDayOrWeekVariableIntervalType } from '../../../../../../../../../core/models/timeblock/time-block-view-type.model';
import { SharedClockHandlerService } from '../../../../../../../../../core/services/features/time-tracking/shared-clock-handler.service';
import { Position } from '../../../../../../../../../shared/data-structures/position';
import { CalendarEvents, CalendarViews } from '../../../../../../../calendar-types';
import { CalendarMouseHandlerService } from '../../../../../../../mouse/calendar-mouse-handler.service';
import { CalendarServiceHelper } from '../../../../../../../services/calendar-service-helper';
import { CalendarService } from '../../../../../../../services/calendar.service';
import { TimeCoordinateMappingService } from '../../../../../../../time-mapping/time-coordinate-mapping.service';
import { DateTimeHelper } from '../../../../../../../util/date-time-helper';
import { TimeBlockGeometryService } from '../../../../../calculation/geometry/time-block-geometry.service';
import { TimeBlockCrudService } from '../../../../../crud/time-block-crud.service';
import { TimeBlockItemBuilderService } from '../../../../../generation/time-block-item-builder.service';
import { TimeBlockDialogService } from '../../../../../services/time-block-dialog.service';
import { TimeBlockService } from '../../../../../services/time-block.service';
import {
  CollisionHandlerService,
  IntersectionType,
} from '../../../collision-handling/collision-handler.service';
import { DragHandlerService } from '../../drag-handler.service';
import { CardinalPoints, DragDirection, DragPart } from '../../drag-part';
import { TimeBlockDragResizeControllerService } from '../../time-block-drag-resize-handling/time-block-drag-resize-controller.service';
import { AbstractResizeController } from '../abstract-resize-controller';
import { ResizeCalculatorService } from '../resize-calculator.service';
import { ResizeLaneSwitchService } from '../resize-lane-switch.service';
import { ResizeTimeBlockScheduleCalculatorService } from '../resize-time-block-schedule-calculator.service';

@Injectable()
export class VerticalDayOrWeekResizeControllerService extends AbstractResizeController {
  public resizeStarted$ = new Subject<DragPart>();

  private calendarBodyHeight = -1;
  private snapDurationInPixels = -1;
  private cursorDeviation = -1; // / Gap between mouse cursor and drag edge.
  private minimumTimeBlockHeight = -1;

  constructor(
    private readonly calendarService: CalendarService,
    private readonly calendarMouseHandlerService: CalendarMouseHandlerService,
    private readonly timeBlockCrudService: TimeBlockCrudService,
    private readonly timeCoordinateMappingService: TimeCoordinateMappingService,
    private readonly timeBlockService: TimeBlockService,
    private readonly timeBlockDialogService: TimeBlockDialogService,
    private readonly timeBlockGeometryService: TimeBlockGeometryService,
    private readonly timeBlockDragResizeControllerService: TimeBlockDragResizeControllerService,
    private readonly collisionHandlerService: CollisionHandlerService,
    private readonly dragHandlerService: DragHandlerService,
    private readonly resizeCalculatorService: ResizeCalculatorService,
    private readonly resizeLaneSwitchService: ResizeLaneSwitchService,
    private readonly resizeTimeBlockScheduleCalculatorService: ResizeTimeBlockScheduleCalculatorService,
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly sharedClockHandlerService: SharedClockHandlerService,
  ) {
    super(
      timeBlockDragResizeControllerService,
      timeBlockDialogService,
      timeBlockCrudService,
      timeBlockItemBuilderService,
    );
    this.initEvents();
  }

  public resize(calendarMousePos: Position, resizeDirection: DragDirection): void {
    let transformationTimeBlock = this.timeBlockDragResizeControllerService.TransformationTimeBlock;

    if (!this.timeBlockDragResizeControllerService.TransformationTimeBlock) {
      return;
    }

    // If user creates a new time block via dragging, drag part is initially undefined.
    if (
      this.timeBlockDragResizeControllerService.DragPart === DragPart.TopOrBottom &&
      resizeDirection.vertical !== CardinalPoints.None
    ) {
      this.timeBlockDragResizeControllerService.DragPart =
        resizeDirection.vertical === CardinalPoints.North ? DragPart.Top : DragPart.Bottom;
    }

    const resizeData = new ResizeData();
    resizeData.mousePos = {
      x: calendarMousePos.x,
      y: calendarMousePos.y,
    };
    resizeData.dragEdgePos = {
      x: calendarMousePos.x,
      y: calendarMousePos.y + this.cursorDeviation,
    };
    resizeData.direction = resizeDirection;

    const visibleMouseLaneIndex = this.calendarMouseHandlerService.calcVisibleMouseLaneIndex(
      calendarMousePos.x,
      null,
      false,
    );
    const minimumTimeBlockDuration =
      this.calendarService.model.calendarProperties.minimumTimeBlockDuration;

    const hitActiveTimeBlockBottomEdge = this.resizeLaneSwitchService.handleLaneSwitch(
      visibleMouseLaneIndex,
      transformationTimeBlock,
      minimumTimeBlockDuration,
    );

    // Skip this resize iteration if time block is active and is resized into the future.
    if (hitActiveTimeBlockBottomEdge) {
      this.timeBlockDragResizeControllerService.DragPart = DragPart.Top;
      CollisionHandlerService.collisionType = IntersectionType.IntersectWithDragBottomEdge;
    }

    resizeData.dragPart = this.timeBlockDragResizeControllerService.DragPart;

    const geometryData = transformationTimeBlock.timeBlockModel.timeBlockViewType.geometryData;
    if (geometryData.height < 0) {
      throw new Error(`Invalid height: ${geometryData.height}`);
    }

    // Re-initialize dragging block to get the updated time block values.
    transformationTimeBlock = this.timeBlockDragResizeControllerService.TransformationTimeBlock;
    if (
      !this.resizeCalculatorService.passedVerticalBound(
        resizeData,
        transformationTimeBlock.timeBlockModel,
      )
    ) {
      return;
    }

    this.collisionHandlerService.checkVerticalIntersections(resizeData);
    this.collisionHandlerService.checkDragEdgeSwitch(
      resizeData,
      transformationTimeBlock.timeBlockModel,
    );

    // Prevent downward resizing of active time block.
    if (
      this.resizeTimeBlockScheduleCalculatorService.hitActiveTimeBlockBottomEdge(
        transformationTimeBlock,
      )
    ) {
      this.updateTimeBlockSchedule();
      return;
    }

    // Remove or add small-time-block-size CSS class
    this.dragHandlerService.toggleSmallTimeBlockCSSClass(transformationTimeBlock);

    // Check collision types
    this.checkCollisionTypes(resizeData);
    // Update schedule
    this.updateTimeBlockSchedule();
  }

  public beforeStartResizing(dragPart: DragPart): void {
    const mouseCalendarPosY =
      this.calendarMouseHandlerService.MouseClickPositionWrapper.mouseCalendarPosition.y;
    const tbModel =
      this.timeBlockDragResizeControllerService.TransformationTimeBlock.timeBlockModel;

    if (dragPart === DragPart.Top) {
      this.cursorDeviation = tbModel.timeBlockViewType.geometryData.top - mouseCalendarPosY;
    } else if (dragPart === DragPart.Bottom) {
      const geometryData = tbModel.timeBlockViewType.geometryData;
      this.cursorDeviation = geometryData.calendarTopEdgeToTimeBlockBottomEdge - mouseCalendarPosY;
    }

    this.timeBlockDragResizeControllerService.DragPart = dragPart;
    this.timeBlockDragResizeControllerService.addCSSClasses(
      dragPart,
      this.timeBlockDragResizeControllerService.TransformationTimeBlock,
    );

    const timeBlockViewType = tbModel.timeBlockViewType as TimeBlockDayOrWeekVariableIntervalType;
    this.resizeLaneSwitchService.lastVisibleMouseLaneIndex =
      CalendarServiceHelper.toVisibleLaneIndex(
        this.calendarService.model,
        timeBlockViewType.laneIndex,
      );
  }

  protected updateTimeBlockSchedule(): void {
    this.resizeTimeBlockScheduleCalculatorService.calculateSchedule();
    this.calendarService.emitCalendarChange(
      this.calendarService.model,
      CalendarEvents.ReplacedTimeBlock,
      this.timeBlockDragResizeControllerService.TransformationTimeBlock,
    );
  }

  private restoreTimeBlockGeometry(): void {
    const geometryData =
      this.timeBlockDragResizeControllerService.TransformationTimeBlock.timeBlockModel
        .timeBlockViewType.geometryData;

    if (CollisionHandlerService.collisionType === IntersectionType.IntersectCalendarTopEdge) {
      geometryData.top = 0;
      geometryData.height = this.calendarBodyHeight - geometryData.bottom;
      geometryData.calendarBottomEdgeToTimeBlockTopEdge = this.calendarBodyHeight;
    } else if (
      CollisionHandlerService.collisionType === IntersectionType.IntersectCalendarBottomEdge
    ) {
      geometryData.bottom = 0;
      geometryData.height = this.calendarBodyHeight - geometryData.top;
      geometryData.calendarTopEdgeToTimeBlockBottomEdge = this.calendarBodyHeight;
    }

    if (geometryData.height <= 0) {
      throw new Error('Height is null.');
    }
  }

  private initEvents(): void {
    this.calendarService.calendarViewInitialized$
      .pipe(
        filter((initialized) => initialized),
        switchMap(() => {
          return combineLatest([
            this.timeBlockService.getMinimumBlockHeight(),
            this.timeCoordinateMappingService.pixelsPerMinuteCalculated$.pipe(
              filter((ppm) => !!ppm),
            ),
          ]);
        }),
        map((response) => response),
      )
      .subscribe(([minBlockHeight, pixelsPerMinute]) => {
        const model = this.calendarService.model as DayOrWeekCalendarModel;

        // The following stuff is only needed for the day or week view.
        if (
          !minBlockHeight ||
          !pixelsPerMinute ||
          model.calendarViewMode === CalendarViews.MonthGrid
        ) {
          return;
        }
        const minimumBlockHeight = minBlockHeight;
        this.snapDurationInPixels =
          model.calendarProperties.timeBlockDraggingDuration * pixelsPerMinute;
        this.calendarBodyHeight = model.geometryData.calendarBodyHeight;
        this.collisionHandlerService.CalendarBodyHeight = this.calendarBodyHeight;
        this.collisionHandlerService.MinimumBlockHeight = minimumBlockHeight;
        this.minimumTimeBlockHeight = minimumBlockHeight;
      });
  }

  private checkCollisionTypes(resizeData: ResizeData) {
    if (
      CollisionHandlerService.collisionType === IntersectionType.IntersectCalendarTopEdge ||
      CollisionHandlerService.collisionType === IntersectionType.IntersectCalendarBottomEdge
    ) {
      // If there is a collision with a calendar edge or a drag handler, reset time block geometry.
      this.restoreTimeBlockGeometry();
      this.updateTimeBlockSchedule();
    } else if (
      CollisionHandlerService.collisionType === IntersectionType.IntersectWithDragTopEdge ||
      CollisionHandlerService.collisionType === IntersectionType.IntersectWithDragBottomEdge
    ) {
      // After the time block geometry was restored in the upper if statement, flip drag edges.
      this.dragHandlerService.flipVerticalDragEdgeSelf(this.minimumTimeBlockHeight);
    } else {
      // No collision
      this.timeBlockGeometryService.calculateVerticalResizeBlockGeometry(
        resizeData,
        this.snapDurationInPixels,
        this.calendarBodyHeight,
      );
    }

    // If the end time is 00:00:00, it means that end date is set to the next day. Therefore, subtract one day.
    // Remark: The end time of 00:00:00 will be set after resizing.
    const end =
      this.timeBlockDragResizeControllerService.TransformationTimeBlock.timeBlockModel.end;
    this.timeBlockDragResizeControllerService.TransformationTimeBlock.timeBlockModel.end =
      DateTimeHelper.transformToNearlyMidnightIfNeeded(end);
  }
}

export class ResizeData {
  public dragPart = DragPart.None;
  public mousePos: Position = {
    x: 0,
    y: 0,
  };
  public dragEdgePos: Position = {
    x: 0,
    y: 0,
  };
  public direction: DragDirection = {
    horizontal: CardinalPoints.None,
    vertical: CardinalPoints.None,
  };
}
